import { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  Section,
  SectionTitle,
  Tooltip,
} from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { text } from 'chaoskit/src/assets/styles/utility'
import { Tabs, TabList, Tab, TabPanel, TabPanels } from '@reach/tabs'
import { StylesButtonReset } from 'chaoskit/src/components/Button'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import BaseLayout from '~layouts/BaseLayout'
import Hero, { HeroContent, HeroTitleWrapper, HeroTitle } from '~partials/Hero'
import Data from '~components/Data'
import { ResponsiveVideo } from '~components/Video'
import heroGrid from '~media/sre/hero-grid.svg'
import Quote from '~components/Quote'
import smallBlob from '~media/sre/small-blob.svg'
import bigBlob from '~media/sre/big-blob.svg'
import Link from '~components/Link'
import Icon from '~components/Icon'
import HelmetSEO from '~partials/global/HelmetSEO'
import ButtonLink from '~components/ButtonLink'
import Image from '~components/Image'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'

const SRETab = (props) => {
  const theme = useTheme()

  const { isSelected } = props

  return (
    <Tab
      css={[
        StylesButtonReset,
        theme.fontSize.large__fluid,
        {
          fontWeight: theme.fontWeight.bold,
          textTransform: 'uppercase',
          transition: `color ${theme.timing.base} ${theme.transition.base}`,

          '&:hover, &:focus': {
            color: theme.color.primary.base,
          },
        },

        isSelected && [
          text.underline,
          {
            cursor: 'default',
            fontWeight: theme.fontWeight.bold,
            color: theme.color.primary.base,
          },
        ],
      ]}
      {...props}
    />
  )
}

SRETab.propTypes = {
  isSelected: PropTypes.bool,
}

const SREGrid = ({ title, blurb, image, url, flip, ...rest }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        [theme.mq.medium]: {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
        },
      }}
      {...rest}
    >
      <Container>
        <div
          css={[
            {
              display: 'grid',
              gap: theme.space.base,
              alignItems: 'center',

              [theme.mq.medium]: {
                gap: theme.space.large,
                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              },
            },
          ]}
        >
          <div
            className="SRE__Grid__ImageContainer"
            css={[
              {
                [theme.mq.small__max]: {
                  textAlign: 'center',
                },

                [theme.mq.medium]: [
                  {
                    gridColumn: flip ? '1 / span 7' : '6 / span 7',
                    gridRow: 1,
                  },

                  flip && {
                    direction: 'rtl',
                  },
                ],
              },
            ]}
          >
            <Image
              src={image}
              css={{
                width: '100%',
                maxWidth: 600,

                [theme.mq.medium]: {
                  maxWidth: 'none',
                  width: 700,
                },
              }}
              alt=""
            />
          </div>

          <div
            css={{
              position: 'relative',

              '&:hover, &:focus': {
                '.SRE__ReadMore__Icon': {
                  transform: `translateX(${theme.space.xsmall}px)`,
                },
              },

              [theme.mq.medium]: {
                gridColumn: flip ? '8 / span 5' : '1 / span 5',
                gridRow: 1,
              },
            }}
          >
            <Link
              to={url}
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
            <SectionTitle
              align={{ medium: 'left' }}
              title={<Data>{title}</Data>}
              sub={<Data>{blurb}</Data>}
            />
            <div
              css={{
                [theme.mq.small__max]: {
                  textAlign: 'center',
                },
              }}
            >
              <div
                css={{
                  fontWeight: theme.fontWeight.bold,
                  color: theme.fontColor.base,
                }}
              >
                <span>Read more</span>
                <Icon
                  className="SRE__ReadMore__Icon"
                  css={{
                    marginLeft: theme.space.small,
                    color: theme.color.primary.base,
                    transition: `transform ${theme.timing.base}  ${theme.transition.bounce}`,
                  }}
                  size="small"
                  icon="arrow-right"
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

SREGrid.propTypes = {
  flip: PropTypes.bool,
  title: PropTypes.string.isRequired,
  blurb: PropTypes.node.isRequired,
  url: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
}

const TriangleSlice = ({ data, index }) => {
  const theme = useTheme()

  return (
    <Tooltip
      placement="top"
      content={
        <div
          css={{ fontSize: theme.fontSize.xsmall }}
          className={theme.settings.classes.trim}
        >
          <Data>{data.blurb}</Data>
        </div>
      }
    >
      <div
        css={[
          {
            background: theme.color.primary.light,
            cursor: 'default',

            '&:hover, &:focus': {
              background: theme.color.primary.base,

              span: {
                color: theme.contrast.base,
              },
            },
          },

          index === 0 && {
            gridRow: '1 / span 2',
          },
        ]}
      >
        <span
          css={{
            display: 'grid',
            height: '100%',
            alignContent: 'end',
            paddingBottom: theme.space.xsmall,
            textAlign: 'center',
            textTransform: 'lowercase',
            fontSize: theme.fontSize['2xsmall'],
            fontWeight: theme.fontWeight.bold,

            [theme.mq.small]: {
              fontSize: theme.fontSize.xsmall,
              fontWeight: theme.fontWeight.bold,
            },

            [theme.mq.medium]: {
              fontSize: theme.fontSize.base,
            },
          }}
        >
          {data.heading}
        </span>
      </div>
    </Tooltip>
  )
}

TriangleSlice.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
}

const SREHierarchyTab = ({ content }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <SectionTitle
        title={<Data>{content.heading}</Data>}
        sub={<Data>{content.blurb}</Data>}
      />

      <div
        css={{
          display: 'grid',
          gap: theme.space.xsmall,
          width: 315,
          height: 210,
          clipPath: 'polygon(50% 0%, 0 100%, 100% 100%)',
          margin: '0 auto',

          [theme.mq.small]: {
            width: 400,
            height: 266.666666667,
          },

          [theme.mq.medium]: {
            width: 600,
            height: 400,
          },
        }}
      >
        {content.items.map((item, index) => (
          <TriangleSlice key={item.heading} data={item} index={index} />
        ))}
      </div>
    </Fragment>
  )
}

SREHierarchyTab.propTypes = {
  content: PropTypes.object.isRequired,
}

const SREsChaosEngineeringTab = ({ content }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <SectionTitle title={<Data>{content.heading}</Data>} />
      <Container size="small">
        <Quote
          contrastQuotes
          className="u-contrast"
          css={{
            background: theme.brand.teal,
            boxShadow: theme.boxShadow.base,
            borderRadius: theme.borderRadius.base,
            padding: theme.space.large,

            [theme.mq.medium]: {
              padding: theme.space.large + theme.space.base,
            },

            blockquote: {
              fontWeight: theme.fontWeight.base,
            },

            '.G__Quote__Author': {
              color: `${theme.contrast.base} !important`,
            },
          }}
        >
          <Data>{content.blurb}</Data>
        </Quote>
      </Container>
    </Fragment>
  )
}

SREsChaosEngineeringTab.propTypes = {
  content: PropTypes.object.isRequired,
}

const MetricsTab = ({ content }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <SectionTitle
        title={<Data>{content.heading}</Data>}
        sub={<Data>{content.blurb}</Data>}
      />
      <div
        css={{
          display: 'grid',
          gap: theme.space.base,

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
        }}
      >
        {content.items.map((item) => (
          <div
            key={item.heading}
            css={{
              border: theme.border.base,
              borderRadius: theme.borderRadius.base,
              boxShadow: theme.boxShadow.base,
              padding: theme.space.medium,

              [theme.mq.medium]: {
                padding: theme.space.large,
              },
            }}
          >
            <h4
              css={{
                marginBottom: theme.space.xsmall,
                color: theme.color.primary.base,
              }}
            >
              {item.heading}
            </h4>
            <div
              css={{
                ...theme.text.large,
                fontWeight: theme.fontWeight.bold,
              }}
            >
              <Data>{item.subHeading}</Data>
            </div>

            <MDXRenderer>{item.content}</MDXRenderer>
          </div>
        ))}
      </div>
    </Fragment>
  )
}

MetricsTab.propTypes = {
  content: PropTypes.object.isRequired,
}

const SiteReliabilityEngineering = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
    articles: { edges: articles },
  },
}) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const theme = useTheme()

  const handleTabChange = (index) => {
    setActiveIndex(index)
  }

  return (
    <BaseLayout
      headerProps={{
        forceCTAContrast: true,
      }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <NodeTypeTracker nodeType="microsite" />
      <Hero
        css={{
          background: `url(${heroGrid}) 0 0, ${generateGradient({
            start: theme.brand.teal,
            stop: theme.color.primary.base,
          })}`,
          backgroundSize: '200px 100px, 100% 100%',
        }}
      >
        <HeroContent>
          <Container>
            <HeroTitleWrapper>
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <div
                css={{
                  ...theme.text.medium__fluid,
                  ...text.expanded(theme),
                  fontWeight: theme.fontWeight.bold,
                  marginTop: theme.space.small,
                }}
              >
                <Data>{pageData.hero.blurb}</Data>
              </div>
            </HeroTitleWrapper>
          </Container>
        </HeroContent>
      </Hero>
      <main css={{ position: 'relative', zIndex: 1 }}>
        <SREGrid
          image={articles[0].node.frontmatter.featuredImage}
          title={
            articles[0].node.frontmatter.landingPageTitle ||
            articles[0].node.frontmatter.title
          }
          blurb={articles[0].node.frontmatter.landingPageBlurb}
          url={articles[0].node.fields.fullUrl}
          flip
          css={{
            position: 'relative',

            '&::after': {
              content: "''",
              background: `url(${bigBlob}) no-repeat 0 0`,
              backgroundSize: 'contain',
              width: 300,
              height: 302,
              zIndex: -1,
              position: 'absolute',
              top: '-15%',
              right: '-10%',
              display: 'none',

              [theme.mq.medium]: {
                display: 'block',
              },
            },

            '.SRE__Grid__ImageContainer': {
              position: 'relative',

              '&::before': {
                content: "''",
                background: `url(${smallBlob}) no-repeat 0 0`,
                backgroundSize: 'contain',
                width: 100,
                height: 66,
                zIndex: 1,
                position: 'absolute',
                top: '-15%',
                left: 50,
                transform: 'translateX(50%)',
                display: 'none',

                [theme.mq.medium]: {
                  display: 'block',
                },
              },

              img: {
                [theme.mq.medium]: {
                  transform: 'translateY(-10%)',
                },
              },
            },
          }}
        />
        <Section>
          <Container>
            <SectionTitle
              as="h2"
              title={<Data>{pageData.incident.heading}</Data>}
              sub={<Data>{pageData.incident.blurb}</Data>}
            />
            <ResponsiveVideo
              title="SRE Best Practices for Incident Management"
              src={pageData.incident.video}
            />
          </Container>
        </Section>
        <Section css={{ background: theme.color.panel.base }}>
          <Container>
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,

                [theme.mq.medium]: {
                  gridTemplateColumns: '2fr 1fr',
                  gap: theme.space.xlarge,
                },
              }}
            >
              <div
                css={{ textAlign: 'center', [theme.mq.medium]: { order: 1 } }}
              >
                <Image
                  src="https://res.cloudinary.com/gremlin/image/upload/v1617736474/Site%20Reliability%20Engineering/whitepaper-promo.png"
                  width="522"
                  height="628"
                  css={{ maxWidth: 300 }}
                  alt=""
                />
              </div>
              <div
                css={{
                  [theme.mq.small__max]: {
                    textAlign: 'center',
                  },
                }}
              >
                <SectionTitle
                  title={<Data>{pageData.bestPractices.heading}</Data>}
                  sub={<Data>{pageData.bestPractices.blurb}</Data>}
                  align={{ medium: 'left' }}
                  css={{
                    marginBottom: `${theme.space.large}px !important`,
                  }}
                />
                <ButtonLink url={pageData.bestPractices.cta.url} type="primary">
                  {pageData.bestPractices.cta.label}
                </ButtonLink>
              </div>
            </div>
          </Container>
        </Section>
        <Section
          space="xlarge"
          css={{ paddingTop: `${theme.space.xlarge}px !important` }}
        >
          <Container>
            <Tabs index={activeIndex} onChange={handleTabChange}>
              <TabList
                css={{
                  display: 'grid',
                  gap: theme.space.base,
                  textAlign: 'center',
                  marginBottom: theme.space.large,

                  [theme.mq.medium]: {
                    gridTemplateColumns: 'repeat(3, auto)',
                    marginBottom: theme.space.xlarge,
                  },
                }}
              >
                <SRETab>{pageData.sreHierarchy.heading}</SRETab>
                <SRETab>{pageData.sreChaos.heading}</SRETab>
                <SRETab>{pageData.metrics.heading}</SRETab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <SREHierarchyTab content={pageData.sreHierarchy} />
                </TabPanel>
                <TabPanel>
                  <SREsChaosEngineeringTab content={pageData.sreChaos} />
                </TabPanel>
                <TabPanel>
                  <MetricsTab content={pageData.metrics} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Container>
        </Section>
        {articles.slice(1).map(({ node }, index) => (
          <SREGrid
            key={node.fileAbsolutePath}
            image={node.frontmatter.featuredImage}
            title={node.frontmatter.landingPageTitle || node.frontmatter.title}
            blurb={node.frontmatter.landingPageBlurb}
            url={node.fields.fullUrl}
            flip={index % 2 !== 0}
          />
        ))}
      </main>
    </BaseLayout>
  )
}

SiteReliabilityEngineering.propTypes = {
  data: PropTypes.object.isRequired,
}

export default SiteReliabilityEngineering

export const pageQuery = graphql`
  query SiteReliabilityEngineeringPageData {
    pageData: file(
      name: { eq: "site-reliability-engineering" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            heading
            blurb
          }
          incident {
            heading
            blurb
            video
          }
          bestPractices {
            heading
            blurb
            cta {
              label
              url
            }
          }
          sreHierarchy {
            heading
            blurb
            items {
              heading
              blurb
            }
          }
          sreChaos {
            heading
            blurb
          }
          metrics {
            heading
            blurb
            items {
              heading
              subHeading
              content
            }
          }
        }
      }
    }

    articles: allMdx(
      sort: { fields: [frontmatter___priority], order: ASC }
      filter: {
        fileAbsolutePath: { regex: "/src/site-reliability-engineering/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...SiteReliabilityEngineeringTeaserFragment
        }
      }
    }
  }
`
